/**
 * Guest Mode Manager
 * Handles localStorage-based draft storage and guest session management
 * for deferred registration model
 */

export interface GuestDraft {
    id: string;
    title: string;
    content: string;
    genre?: string;
    targetAudience?: string;
    chapters?: Array<{
      id: string;
      title: string;
      content: string;
      order: number;
    }>;
    coverData?: {
      backgroundColor: string;
      titleText: string;
      authorText: string;
      imageUrl?: string;
    };
    createdAt: number;
    updatedAt: number;
    wordCount: number;
  }
  
  export interface GuestSession {
    guestId: string;
    createdAt: number;
    totalWordCount: number;
    draftsCreated: number;
    xpEarned: number;
    lastActiveAt: number;
  }
  
  const GUEST_SESSION_KEY = 'scriblish_guest_session';
  const GUEST_DRAFTS_KEY = 'scriblish_guest_drafts';
  const MAX_GUEST_DRAFTS = 5; // Limit to encourage registration
  
  export class GuestModeManager {
    private static instance: GuestModeManager;
  
    private constructor() {
      this.initializeGuestSession();
    }
  
    static getInstance(): GuestModeManager {
      if (!GuestModeManager.instance) {
        GuestModeManager.instance = new GuestModeManager();
      }
      return GuestModeManager.instance;
    }
  
    /**
     * Initialize or retrieve existing guest session
     */
    private initializeGuestSession(): GuestSession {
      const existing = localStorage.getItem(GUEST_SESSION_KEY);
      
      if (existing) {
        const session = JSON.parse(existing) as GuestSession;
        // Update last active
        session.lastActiveAt = Date.now();
        localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
        return session;
      }
  
      // Create new guest session
      const newSession: GuestSession = {
        guestId: this.generateGuestId(),
        createdAt: Date.now(),
        totalWordCount: 0,
        draftsCreated: 0,
        xpEarned: 0,
        lastActiveAt: Date.now()
      };
  
      localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(newSession));
      return newSession;
    }
  
    /**
     * Generate unique guest ID
     */
    private generateGuestId(): string {
      return `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  
    /**
     * Get current guest session
     */
    getGuestSession(): GuestSession | null {
      const session = localStorage.getItem(GUEST_SESSION_KEY);
      return session ? JSON.parse(session) : null;
    }
  
    /**
     * Check if user is in guest mode
     */
    isGuestMode(): boolean {
      return !!this.getGuestSession() && !this.isRegistered();
    }
  
    /**
     * Check if user has registered (stub - integrate with your auth)
     */
    private isRegistered(): boolean {
      // This should check your actual auth state
      // For now, checking if there's a user in localStorage or Firebase
      return false; // Replace with actual auth check
    }
  
    /**
     * Save a draft to localStorage
     */
    saveDraft(draft: Omit<GuestDraft, 'id' | 'createdAt' | 'updatedAt'>): string {
      const drafts = this.getAllDrafts();
      
      // Check if we've hit the guest limit
      if (drafts.length >= MAX_GUEST_DRAFTS) {
        throw new Error('GUEST_LIMIT_REACHED');
      }
  
      const newDraft: GuestDraft = {
        ...draft,
        id: this.generateDraftId(),
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
  
      drafts.push(newDraft);
      localStorage.setItem(GUEST_DRAFTS_KEY, JSON.stringify(drafts));
  
      // Update session stats
      this.updateSessionStats({
        draftsCreated: 1,
        totalWordCount: draft.wordCount,
        xpEarned: this.calculateXP(draft.wordCount)
      });
  
      return newDraft.id;
    }
  
    /**
     * Update an existing draft
     */
    updateDraft(draftId: string, updates: Partial<GuestDraft>): void {
      const drafts = this.getAllDrafts();
      const index = drafts.findIndex(d => d.id === draftId);
  
      if (index === -1) {
        throw new Error('Draft not found');
      }
  
      const oldWordCount = drafts[index].wordCount;
      drafts[index] = {
        ...drafts[index],
        ...updates,
        updatedAt: Date.now()
      };
  
      localStorage.setItem(GUEST_DRAFTS_KEY, JSON.stringify(drafts));
  
      // Update session stats if word count changed
      if (updates.wordCount !== undefined) {
        const wordCountDelta = updates.wordCount - oldWordCount;
        this.updateSessionStats({
          totalWordCount: wordCountDelta,
          xpEarned: this.calculateXP(wordCountDelta)
        });
      }
    }
  
    /**
     * Get all guest drafts
     */
    getAllDrafts(): GuestDraft[] {
      const drafts = localStorage.getItem(GUEST_DRAFTS_KEY);
      return drafts ? JSON.parse(drafts) : [];
    }
  
    /**
     * Get a specific draft
     */
    getDraft(draftId: string): GuestDraft | null {
      const drafts = this.getAllDrafts();
      return drafts.find(d => d.id === draftId) || null;
    }
  
    /**
     * Delete a draft
     */
    deleteDraft(draftId: string): void {
      const drafts = this.getAllDrafts();
      const filtered = drafts.filter(d => d.id !== draftId);
      localStorage.setItem(GUEST_DRAFTS_KEY, JSON.stringify(filtered));
    }
  
    /**
     * Update guest session statistics
     */
    private updateSessionStats(delta: {
      draftsCreated?: number;
      totalWordCount?: number;
      xpEarned?: number;
    }): void {
      const session = this.getGuestSession();
      if (!session) return;
  
      session.draftsCreated += delta.draftsCreated || 0;
      session.totalWordCount += delta.totalWordCount || 0;
      session.xpEarned += delta.xpEarned || 0;
      session.lastActiveAt = Date.now();
  
      localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
    }
  
    /**
     * Calculate XP from word count (match your existing XP system)
     */
    private calculateXP(wordCount: number): number {
      // Basic XP calculation - adjust to match your system
      return Math.floor(wordCount / 10);
    }
  
    /**
     * Generate unique draft ID
     */
    private generateDraftId(): string {
      return `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  
    /**
     * Check if guest has reached limits (to trigger registration prompts)
     */
    shouldPromptRegistration(): {
      shouldPrompt: boolean;
      reason?: 'DRAFT_LIMIT' | 'WORD_COUNT' | 'XP_MILESTONE';
    } {
      const session = this.getGuestSession();
      const drafts = this.getAllDrafts();
  
      if (!session) return { shouldPrompt: false };
  
      // Approaching draft limit
      if (drafts.length >= MAX_GUEST_DRAFTS - 1) {
        return { shouldPrompt: true, reason: 'DRAFT_LIMIT' };
      }
  
      // Significant word count milestone
      if (session.totalWordCount >= 1000 && session.totalWordCount < 1100) {
        return { shouldPrompt: true, reason: 'WORD_COUNT' };
      }
  
      // XP milestone
      if (session.xpEarned >= 500 && session.xpEarned < 550) {
        return { shouldPrompt: true, reason: 'XP_MILESTONE' };
      }
  
      return { shouldPrompt: false };
    }
  
    /**
     * Get data for migration to Firebase on registration
     */
    getMigrationData(): {
      drafts: GuestDraft[];
      session: GuestSession;
    } | null {
      const session = this.getGuestSession();
      const drafts = this.getAllDrafts();
  
      if (!session) return null;
  
      return { drafts, session };
    }
  
    /**
     * Clear guest data after successful migration
     */
    clearGuestData(): void {
      localStorage.removeItem(GUEST_SESSION_KEY);
      localStorage.removeItem(GUEST_DRAFTS_KEY);
    }
  
    /**
     * Get storage usage info for display
     */
    getStorageInfo(): {
      draftCount: number;
      maxDrafts: number;
      totalWordCount: number;
      xpEarned: number;
    } {
      const session = this.getGuestSession();
      const drafts = this.getAllDrafts();
  
      return {
        draftCount: drafts.length,
        maxDrafts: MAX_GUEST_DRAFTS,
        totalWordCount: session?.totalWordCount || 0,
        xpEarned: session?.xpEarned || 0
      };
    }
  }
  
  export default GuestModeManager.getInstance();
  